import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    ChevronLeft,
    ShieldCheck,
    Globe,
    Leaf,
    Award,
    FileText,
    ExternalLink,
    Info,
    MapPin,
    ArrowRight,
    Target,
    Zap,
    Users,
    Activity,
    ChevronDown,
    Link2,
    Heart,
    Share2,
    Building,
    CheckCircle2,
    Database,
    Clock,
    QrCode,
    Calculator,
    TreePine,
    Car,
    Home,
    Plane,
    Droplet
} from 'lucide-react';
import {
    RegistryVerification,
    BatchViewer,
    VaultPanel,
    ImpactCalculator,
    PricingPanel,
    BasketInterface,
    PortfolioDashboard,
    RetirementCertificate
} from './components';

// Mock Data
const MOCK_CARBON_CREDITS = [
    {
        id: 401,
        title: 'Amazon Rainforest Conservation',
        tokenSymbol: 'AMZN-CRN',
        category: 'CARBON_CREDITS',
        projectType: 'Forestry',
        standard: 'Gold Standard',
        vintage: '2020',
        creditType: 'Nature-Based',
        esgScore: 'A+',
        roi: '15%',
        cagr: '18.00%',
        issuerName: 'Rainforest Trust',
        issuerLogo: '/assets/carbon/rainforest-trust-logo.png',
        assetPrice: 'AED 5M',
        tokenPriceETH: '0.8 ETH',
        tokenPriceAED: '2,400 AED',
        availableTokens: 100000,
        totalTokens: 250000,
        status: 'open',
        image: 'https://images.unsplash.com/photo-1511497584788-876760111969?w=800&h=600&fit=crop',
        badge: 'OPEN',
        launchDate: 'Live Now',
        progress: 60,
        investmentStrategy: 'Long-Term Impact',
        co2Volume: '50,000 t/year',
        sdgAlignment: [
            { name: 'No Poverty', icon: '/assets/E-SDG-Icons-WEB/E SDG Icons WEB/E-WEB-Goal-01.avif' },
            { name: 'Climate Action', icon: '/assets/E-SDG-Icons-WEB/E SDG Icons WEB/E-WEB-Goal-13.avif' },
            { name: 'Life on Land', icon: '/assets/E-SDG-Icons-WEB/E SDG Icons WEB/E-WEB-Goal-15.avif' }
        ],
        projectLocation: 'Para State, Brazil',
        permanencePeriod: '100 years',
        methodology: 'VM0007 / REDD+',
        additionalityStatement: 'Prevents 45% annual deforestation risk.',
        sequestrationType: 'Avoidance',
        registryID: 'VCS-2451',
        onChainID: 'TCO2-VCS-2451-2020',
        explorerURL: 'https://polygonscan.com/token/0x...',
        projectScale: '2.5M Tonnes',
        riskRating: 'AAA (Sylvera)',
        coordinates: { lat: -3.46, lng: -62.21 },
        fieldGallery: [
            'https://images.unsplash.com/photo-1511497584788-876760111969',
            'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5',
            'https://images.unsplash.com/photo-1542273917363-3b1817f69a2d'
        ],
        verificationDocs: [
            { title: 'Validation Report 2023', url: '#' },
            { title: 'Monitoring Report Q4', url: '#' },
            { title: 'Registry Certificate', url: '#' }
        ],
        registryName: 'Verra (VCS)',
        registryUrl: 'https://registry.verra.org/app/projectDetail/VCS/2451',
        verificationBody: 'SCS Global Services',
        projectStatus: 'Active',
        verificationStatus: 'Verified',
        creditsIssued: 250000,
        creditsAvailable: 100000,
        batches: [
            {
                batch_id: 'VCS-2451-2020-B001',
                vintage_year: 2020,
                credit_quantity: 150000,
                serial_number_start: 'VCS-2451-2020-000001',
                serial_number_end: 'VCS-2451-2020-150000',
                issuance_date: '2020-03-15',
                status: 'Active'
            },
            {
                batch_id: 'VCS-2451-2021-B002',
                vintage_year: 2021,
                credit_quantity: 100000,
                serial_number_start: 'VCS-2451-2021-000001',
                serial_number_end: 'VCS-2451-2021-100000',
                issuance_date: '2021-04-20',
                status: 'Active'
            }
        ],
        vault: {
            vault_id: 'VAULT-AMZN-001',
            total_capacity: 250000,
            tokens_issued: 250000,
            tokens_circulating: 150000,
            credits_retired: 50000,
            custodian: 'Rainforest Trust Custody'
        },
        pricing: {
            base_price: 2000,
            quality_multiplier: 1.5,
            vintage_adjustment: 0.12,
            demand_factor: 1.16,
            type_premium: 1.24,
            final_price: 4050,
            price_history: [
                { date: '2026-02-12', price: 3800 },
                { date: '2026-02-19', price: 3900 },
                { date: '2026-02-26', price: 3950 },
                { date: '2026-03-05', price: 4000 },
                { date: '2026-03-12', price: 4050 }
            ]
        },
        investmentHighlights: [
            { title: 'Immediate Climate Impact', description: 'Every credit directly enables the preservation of endangered rainforest ecosystems.' },
            { title: 'Community Support', description: 'Funds 25+ local conservation jobs and sustainable education programs.' },
            { title: 'Verified Additionality', description: 'Third-party audited to ensure your impact is measurable and real.' }
        ]
    },
    {
        id: 402,
        title: 'Solar Farm Carbon Offset',
        tokenSymbol: 'SOLAR-CO2',
        category: 'CARBON_CREDITS',
        projectType: 'Renewable Energy',
        standard: 'Verra (VCS)',
        vintage: '2022',
        creditType: 'Tech-Based',
        esgScore: 'A',
        roi: '12%',
        cagr: '14.50%',
        issuerName: 'Clean Energy Fund',
        issuerLogo: 'https://ui-avatars.com/api/?name=Clean+Energy&background=F59E0B&color=fff',
        assetPrice: 'AED 11M',
        tokenPriceETH: '0.5 ETH',
        tokenPriceAED: '1,500 AED',
        availableTokens: 80000,
        totalTokens: 200000,
        status: 'open',
        image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800&h=600&fit=crop',
        badge: 'OPEN',
        launchDate: 'Live Now',
        progress: 60,
        investmentStrategy: 'Yield via Offtake',
        co2Volume: '30,000 t/year',
        sdgAlignment: [
            { name: 'Affordable and Clean Energy', icon: '/assets/E-SDG-Icons-WEB/E SDG Icons WEB/E-WEB-Goal-07.avif' },
            { name: 'Industry, Innovation and Infrastructure', icon: '/assets/E-SDG-Icons-WEB/E SDG Icons WEB/E-WEB-Goal-09.avif' },
            { name: 'Climate Action', icon: '/assets/E-SDG-Icons-WEB/E SDG Icons WEB/E-WEB-Goal-13.avif' }
        ],
        projectLocation: 'Rajasthan, India',
        permanencePeriod: '25 years',
        methodology: 'ACM0002 / Grid Renewables',
        additionalityStatement: 'Displaces coal-based power generation.',
        sequestrationType: 'Avoidance (Tech)',
        registryID: 'Verra-1892',
        onChainID: 'TCO2-VCS-1892-2022',
        explorerURL: 'https://polygonscan.com/token/0x...',
        projectScale: '1.2M Tonnes',
        riskRating: 'AA (BeZero)',
        coordinates: { lat: 26.91, lng: 70.9 },
        fieldGallery: [
            'https://images.unsplash.com/photo-1509391366360-2e959784a276',
            'https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9',
            'https://images.unsplash.com/photo-1466611653911-95281773ad90'
        ],
        verificationDocs: [
            { title: 'VCS Verification Statement', url: '#' },
            { title: 'Annual Energy Report', url: '#' }
        ],
        registryName: 'Verra (VCS)',
        registryUrl: 'https://registry.verra.org/app/projectDetail/VCS/1892',
        verificationBody: 'TÜV SÜD',
        projectStatus: 'Active',
        verificationStatus: 'Verified',
        creditsIssued: 200000,
        creditsAvailable: 80000,
        batches: [
            {
                batch_id: 'VCS-1892-2022-B001',
                vintage_year: 2022,
                credit_quantity: 120000,
                serial_number_start: 'VCS-1892-2022-000001',
                serial_number_end: 'VCS-1892-2022-120000',
                issuance_date: '2022-06-10',
                status: 'Active'
            },
            {
                batch_id: 'VCS-1892-2023-B002',
                vintage_year: 2023,
                credit_quantity: 80000,
                serial_number_start: 'VCS-1892-2023-000001',
                serial_number_end: 'VCS-1892-2023-80000',
                issuance_date: '2023-07-15',
                status: 'Active'
            }
        ],
        vault: {
            vault_id: 'VAULT-SOLAR-001',
            total_capacity: 200000,
            tokens_issued: 200000,
            tokens_circulating: 120000,
            credits_retired: 40000,
            custodian: 'Clean Energy Fund Custody'
        },
        pricing: {
            base_price: 1500,
            quality_multiplier: 1.3,
            vintage_adjustment: 0.06,
            demand_factor: 1.16,
            type_premium: 1.0,
            final_price: 2380,
            price_history: [
                { date: '2026-02-12', price: 2200 },
                { date: '2026-02-19', price: 2250 },
                { date: '2026-02-26', price: 2300 },
                { date: '2026-03-05', price: 2350 },
                { date: '2026-03-12', price: 2380 }
            ]
        },
        investmentHighlights: [
            { title: 'Immediate Climate Impact', description: 'Every credit directly enables clean energy production.' },
            { title: 'Community Support', description: 'Powers 50,000+ homes with renewable energy.' },
            { title: 'Verified Additionality', description: 'Third-party audited to ensure your impact is measurable and real.' }
        ]
    }
];

const MOCK_CARBON_BASKETS = [
    {
        basket_id: 'BASKET-DIV-001',
        basket_name: 'Diversified Carbon Basket #1',
        description: 'A balanced portfolio of nature-based and tech-based carbon credits',
        total_capacity: 500000,
        tokens_available: 250000,
        token_price: 2000,
        diversification_score: 'A+',
        risk_rating: 'AA',
        expected_roi: '14%',
        composition: [
            {
                project_id: 401,
                project_name: 'Amazon Rainforest Conservation',
                allocation_percentage: 40,
                credit_type: 'Nature-Based',
                vintage: 2020,
                image: 'https://images.unsplash.com/photo-1511497584788-876760111969?w=100&h=100&fit=crop'
            },
            {
                project_id: 402,
                project_name: 'Solar Farm Carbon Offset',
                allocation_percentage: 30,
                credit_type: 'Tech-Based',
                vintage: 2022,
                image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=100&h=100&fit=crop'
            },
            {
                project_id: 403,
                project_name: 'Wind Farm Brazil',
                allocation_percentage: 20,
                credit_type: 'Tech-Based',
                vintage: 2021,
                image: 'https://images.unsplash.com/photo-1466611653911-95281773ad90?w=100&h=100&fit=crop'
            },
            {
                project_id: 404,
                project_name: 'Mangrove Restoration',
                allocation_percentage: 10,
                credit_type: 'Nature-Based',
                vintage: 2023,
                image: 'https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?w=100&h=100&fit=crop'
            }
        ],
        sdg_alignment: [
            { name: 'Climate Action', icon: '/assets/E-SDG-Icons-WEB/E SDG Icons WEB/E-WEB-Goal-13.avif' },
            { name: 'Life on Land', icon: '/assets/E-SDG-Icons-WEB/E SDG Icons WEB/E-WEB-Goal-15.avif' },
            { name: 'Clean Energy', icon: '/assets/E-SDG-Icons-WEB/E SDG Icons WEB/E-WEB-Goal-07.avif' }
        ],
        benefits: [
            'Instant diversification across 4 projects',
            'Reduced risk through portfolio allocation',
            'Lower entry point than individual projects',
            'Automatic rebalancing quarterly'
        ]
    }
];

export default function CarbonCreditsDetailPage() {
    const { id } = useParams();
    const [project, setProject] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [retirementAmount, setRetirementAmount] = useState('');
    const [beneficiary, setBeneficiary] = useState('');
    const [isFaqOpen, setIsFaqOpen] = useState(null);
    const [isWishlisted, setIsWishlisted] = useState(false);
    const [showCertificate, setShowCertificate] = useState(false);
    const [retirementCertificate, setRetirementCertificate] = useState(null);
    const [impactTonnes, setImpactTonnes] = useState('10');

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: project.title,
                url: window.location.href
            });
        } else {
            navigator.clipboard.writeText(window.location.href);
            alert('Link copied to clipboard!');
        }
    };

    const handleRetire = () => {
        if (!retirementAmount || !beneficiary) return;

        const certificate = {
            certificate_id: `CERT-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 999999)).padStart(6, '0')}`,
            beneficiary_name: beneficiary,
            project_name: project.title,
            project_id: project.id,
            co2_offset_tonnes: parseInt(retirementAmount),
            retirement_date: new Date().toISOString(),
            registry_reference: project.registryID,
            registry_url: project.registryUrl,
            blockchain_tx_hash: `0x${Array(40).fill(0).map(() => Math.floor(Math.random() * 16).toString(16)).join('')}`,
            qr_code_url: `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${project.registryUrl}`,
            status: 'Verified'
        };

        setRetirementCertificate(certificate);
        setShowCertificate(true);
    };

    useEffect(() => {
        const found = MOCK_CARBON_CREDITS.find(p => p.id === parseInt(id));
        setProject(found || MOCK_CARBON_CREDITS[0]);
        window.scrollTo(0, 0);
    }, [id]);

    const renderOverview = (project) => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6 uppercase tracking-wide">Project Strategy</h3>
                <p className="text-gray-600 leading-relaxed">
                    {project.additionalityStatement} This project operates under the high-integrity {project.standard} framework, ensuring every credit represents a real, measurable emission reduction.
                </p>
                <div className="flex items-center gap-6 py-6">
                    <div className="p-3 bg-white rounded-xl shadow-sm">
                        <ShieldCheck className="text-emerald-600" size={24} />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Quality Rating</p>
                        <p className="text-lg font-black text-[#1f6f6f]">{project.riskRating}</p>
                    </div>
                </div>
            </div>
            <div className="space-y-6">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">SDG Alignment</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {project.sdgAlignment.map((sdg, i) => (
                        <div key={i} className="flex items-center gap-4 py-4 transition-all group">
                            <div className="w-14 h-14 bg-gray-50 rounded-xl flex items-center justify-center p-1 transition-colors">
                                <img src={sdg.icon} alt={sdg.name} className="w-full h-full object-contain" />
                            </div>
                            <span className="text-[10px] font-black text-gray-800 uppercase tracking-widest">{sdg.name}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderMethodology = (project) => (
        <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Quantification Methodology</h4>
                    <p className="text-xl font-black text-gray-900 uppercase tracking-tight">{project.methodology}</p>
                </div>
                <div>
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Sequestration Mode</h4>
                    <p className="text-xl font-black text-gray-900 uppercase tracking-tight">{project.sequestrationType}</p>
                </div>
                <div>
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Registry ID</h4>
                    <p className="text-xl font-black text-[#1f6f6f] uppercase tracking-tight">{project.registryID}</p>
                </div>
                <div>
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Location Context</h4>
                    <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-emerald-600" />
                        <p className="text-sm font-bold text-gray-800 uppercase tracking-wide">{project.projectLocation}</p>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderGallery = (project) => (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {project.fieldGallery.map((img, i) => (
                <div key={i} className="aspect-square rounded-2xl overflow-hidden hover:ring-4 ring-emerald-500/20 transition-all cursor-zoom-in">
                    <img src={img} alt="field site" className="w-full h-full object-cover" />
                </div>
            ))}
        </div>
    );

    const renderDocuments = (project) => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {project.verificationDocs.map((doc, i) => (
                <a
                    key={i}
                    href={doc.url}
                    className="flex items-center justify-between py-6 border-b border-gray-50 hover:border-emerald-600 transition-all group"
                >
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-gray-50 rounded-xl group-hover:bg-emerald-50 transition-colors">
                            <FileText className="text-gray-400 group-hover:text-emerald-600" />
                        </div>
                        <span className="text-xs font-black uppercase tracking-widest text-gray-800">{doc.title}</span>
                    </div>
                    <ExternalLink size={18} className="text-gray-300 group-hover:text-emerald-600 transition-colors" />
                </a>
            ))}
        </div>
    );

    if (!project) return null;

    return (
        <div className="min-h-screen bg-white">
            {/* Navigation & Actions */}
            <div className="bg-white border-b border-gray-100 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link
                        to="/marketplace"
                        className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors font-medium text-sm md:text-base"
                    >
                        <ChevronLeft size={20} />
                        Back to Marketplace
                    </Link>

                    <div className="flex gap-3">
                        <button
                            onClick={() => setIsWishlisted(!isWishlisted)}
                            className={`p-2 rounded-sm border transition-all ${isWishlisted
                                ? 'bg-red-50 border-red-200 text-red-500'
                                : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                                } `}
                        >
                            <Heart size={18} fill={isWishlisted ? 'currentColor' : 'none'} />
                        </button>
                        <button
                            onClick={handleShare}
                            className="p-2 bg-white border border-gray-200 rounded-sm text-gray-500 hover:bg-gray-50 transition-all"
                        >
                            <Share2 size={18} />
                        </button>
                    </div>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-4 py-8 lg:py-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

                    {/* Left Column - Project Content */}
                    <div className="lg:col-span-8 space-y-12">

                        {/* Hero Section */}
                        <section className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                            <div className="relative aspect-[21/9] rounded-3xl overflow-hidden shadow-2xl group border border-gray-100">
                                <img
                                    src={project.image}
                                    alt={project.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <span className={`inline-flex px-3 py-1 rounded-sm text-[10px] font-semibold tracking-widest mb-4 border border-emerald-200 text-emerald-700`}>
                                        {project.badge}
                                    </span>

                                    <h1 className="text-[22px] sm:text-3xl lg:text-5xl font-bold text-[#0F172A] leading-tight mb-4 lowercase first-letter:uppercase whitespace-nowrap overflow-x-auto no-scrollbar pb-1 tracking-tight sm:tracking-normal">
                                        {project.title}
                                    </h1>

                                    <div className="flex flex-wrap items-center gap-y-2 text-gray-500 mb-6 font-medium">
                                        <div className="flex items-center gap-2 pr-4">
                                            <MapPin size={18} className="text-[#15a36e]" />
                                            <span className="text-base">{project.projectLocation}</span>
                                        </div>
                                        <div className="hidden sm:block h-4 w-px bg-gray-300 mr-4" />
                                        <div className="flex items-center gap-4 text-sm sm:text-base">
                                            <div className="flex items-center gap-1.5">
                                                <Building size={16} className="text-[#15a36e]" />
                                                <span className="font-semibold text-gray-700">{project.issuerName}</span>
                                            </div>
                                            <div className="h-4 w-px bg-gray-300" />
                                            <span>{project.projectType} Project</span>
                                            <div className="h-4 w-px bg-gray-300" />
                                            <span>{project.vintage} Vintage</span>
                                        </div>
                                    </div>

                                    {/* Pricing Summary */}
                                    <div className="flex items-center gap-4 mb-4 w-full overflow-x-auto no-scrollbar pb-2 sm:pb-0">
                                        <div className="px-4 sm:px-5 py-3 rounded-sm border border-gray-100 flex items-center gap-4 sm:gap-6 group transition-all duration-300 min-w-max">
                                            <div className="flex flex-col">
                                                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide leading-none mb-2">Price per tonne</span>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xl sm:text-2xl font-bold tracking-tight text-[#0F172A] group-hover:text-emerald-600 transition-colors uppercase">AED {project.tokenPriceAED.split(' ')[0]}</span>
                                                    <span className="text-[9px] sm:text-[10px] text-emerald-600 font-bold uppercase tracking-wide border border-emerald-100 px-2 py-0.5 rounded-sm">/ Tonne</span>
                                                </div>
                                            </div>
                                            <div className="h-10 w-px bg-gray-100" />
                                            <div className="flex flex-col">
                                                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide leading-none mb-2">Target ROI</span>
                                                <span className="text-base sm:text-lg font-bold text-emerald-600 uppercase tabular-nums">{project.roi}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Positive Impact Banner */}
                        <section className="py-6 flex items-start gap-4">
                            <div className="text-emerald-600 shrink-0">
                                <CheckCircle2 size={24} />
                            </div>
                            <div className="flex-1">
                                <h4 className="text-sm font-black text-emerald-900 uppercase tracking-tight mb-1">You're making a positive impact!</h4>
                                <p className="text-xs text-emerald-700 leading-relaxed font-medium">
                                    These carbon credits are independently verified and will be retired from circulation.
                                    <button className="ml-2 text-emerald-900 font-black underline underline-offset-4 hover:text-emerald-950">What does retire mean?</button>
                                </p>
                            </div>
                        </section>

                        {/* Impact Metrics */}
                        <section className="bg-black border border-white/10 rounded-xl overflow-hidden relative shadow-2xl p-6 md:p-8">
                            <div className="absolute inset-0 w-full h-full overflow-hidden z-0 pointer-events-none">
                                <img
                                    src="/carbon_credits_graphic.png"
                                    alt="Impact Background"
                                    className="absolute inset-x-0 inset-y-0 w-full h-full object-cover object-center translate-x-1/4 scale-110 opacity-80"
                                />
                                <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-black via-black/90 to-black/20" />
                                <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-black/40 via-transparent to-black/40" />
                            </div>

                            <div className="relative z-10">
                                <h2 className="text-xl font-bold text-white mb-8 flex items-center justify-start uppercase tracking-wide">
                                    IMPACT METRICS
                                </h2>

                                <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
                                    <div className="bg-white/5 border border-white/10 rounded-sm p-4 shadow-sm transition-all hover:bg-white/10 flex flex-col justify-center">
                                        <div className="flex justify-between items-start mb-2">
                                            <span className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">CO₂ Impact</span>
                                            <Leaf size={14} className="text-emerald-400" />
                                        </div>
                                        <div className="text-xl font-extrabold text-white tracking-tight leading-none uppercase">
                                            {project.co2Volume}
                                        </div>
                                    </div>

                                    <div className="bg-white/5 border border-white/10 rounded-sm p-4 shadow-sm transition-all hover:bg-white/10 flex flex-col justify-center">
                                        <div className="flex justify-between items-start mb-2">
                                            <span className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">Permanence</span>
                                            <Globe size={14} className="text-blue-400" />
                                        </div>
                                        <div className="text-xl font-extrabold text-white tracking-tight leading-none uppercase">
                                            {project.permanencePeriod}
                                        </div>
                                    </div>

                                    <div className="bg-white/5 border border-white/10 rounded-sm p-4 shadow-sm transition-all hover:bg-white/10 flex flex-col justify-center col-span-2 lg:col-span-1">
                                        <div className="flex justify-between items-start mb-2">
                                            <span className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">Project Scale</span>
                                            <Award size={14} className="text-purple-400" />
                                        </div>
                                        <div className="text-xl font-extrabold text-white tracking-tight leading-none uppercase">
                                            {project.projectScale}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Why Invest Section */}
                        {project.investmentHighlights && (
                            <section className="bg-black rounded-xl overflow-hidden relative border border-white/10 shadow-2xl">
                                <div className="absolute inset-0 w-full h-full overflow-hidden z-0 pointer-events-none">
                                    <img
                                        src="/carbon_credits_graphic.png"
                                        alt="Project Highlights"
                                        className="absolute inset-x-0 inset-y-0 w-full h-full object-cover object-center translate-x-1/4 scale-110 opacity-80"
                                    />
                                    <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-black via-black/90 to-black/20" />
                                    <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-black/40 via-transparent to-black/40" />
                                </div>

                                <div className="relative z-10 p-8 md:p-12">
                                    <div className="max-w-3xl">
                                        <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight leading-tight uppercase mb-10">
                                            Why support this project?
                                        </h2>

                                        <div className="grid grid-cols-1 gap-y-8">
                                            {project.investmentHighlights.map((item, idx) => (
                                                <div key={idx} className="space-y-2">
                                                    <h3 className="text-white text-lg font-black tracking-tight uppercase">{item.title}</h3>
                                                    <p className="text-gray-400 text-sm leading-relaxed font-bold">{item.description}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-600/10 blur-[100px] -mr-32 -mt-32 pointer-events-none" />
                            </section>
                        )}

                        {/* All Sections Sequentially */}
                        <section className="space-y-16">
                            <div id="overview" className="space-y-8 scroll-mt-32">
                                <div className="flex items-center gap-4">
                                    <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide">Overview</h2>
                                    <div className="h-px flex-1 bg-gray-100" />
                                </div>
                                {renderOverview(project)}
                            </div>

                            <div id="methodology" className="space-y-8 scroll-mt-32">
                                <div className="flex items-center gap-4">
                                    <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide">Methodology</h2>
                                    <div className="h-px flex-1 bg-gray-100" />
                                </div>
                                {renderMethodology(project)}
                            </div>

                            {/* Component-based Sections */}
                            <div id="registry" className="space-y-8 scroll-mt-32">
                                <div className="flex items-center gap-4">
                                    <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide">Registry Verification</h2>
                                    <div className="h-px flex-1 bg-gray-100" />
                                </div>
                                <RegistryVerification project={project} />
                            </div>

                            <div id="batches" className="space-y-8 scroll-mt-32">
                                <div className="flex items-center gap-4">
                                    <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide">Credit Batches</h2>
                                    <div className="h-px flex-1 bg-gray-100" />
                                </div>
                                <BatchViewer project={project} />
                            </div>

                            <div id="vault" className="space-y-8 scroll-mt-32">
                                <div className="flex items-center gap-4">
                                    <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide">Vault Transparency</h2>
                                    <div className="h-px flex-1 bg-gray-100" />
                                </div>
                                <VaultPanel project={project} />
                            </div>

                            <div id="calculator" className="space-y-8 scroll-mt-32">
                                <div className="flex items-center gap-4">
                                    <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide">Impact Calculator</h2>
                                    <div className="h-px flex-1 bg-gray-100" />
                                </div>
                                <ImpactCalculator
                                    impactTonnes={impactTonnes}
                                    setImpactTonnes={setImpactTonnes}
                                    setRetirementAmount={setRetirementAmount}
                                />
                            </div>

                            <div id="pricing" className="space-y-8 scroll-mt-32">
                                <div className="flex items-center gap-4">
                                    <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide">Price Breakdown</h2>
                                    <div className="h-px flex-1 bg-gray-100" />
                                </div>
                                <PricingPanel project={project} />
                            </div>

                            <div id="basket" className="space-y-8 scroll-mt-32">
                                <div className="flex items-center gap-4">
                                    <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide">Diversified Basket</h2>
                                    <div className="h-px flex-1 bg-gray-100" />
                                </div>
                                <BasketInterface basket={MOCK_CARBON_BASKETS[0]} />
                            </div>

                            <div id="portfolio" className="space-y-8 scroll-mt-32">
                                <div className="flex items-center gap-4">
                                    <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide">Portfolio Impact</h2>
                                    <div className="h-px flex-1 bg-gray-100" />
                                </div>
                                <PortfolioDashboard />
                            </div>

                            <div id="gallery" className="space-y-8 scroll-mt-32">
                                <div className="flex items-center gap-4">
                                    <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide">Gallery</h2>
                                    <div className="h-px flex-1 bg-gray-100" />
                                </div>
                                {renderGallery(project)}
                            </div>

                            <div id="documents" className="space-y-8 scroll-mt-32">
                                <div className="flex items-center gap-4">
                                    <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide">Documents</h2>
                                    <div className="h-px flex-1 bg-gray-100" />
                                </div>
                                {renderDocuments(project)}
                            </div>
                        </section>
                    </div>

                    {/* Right Column - Retirement Workflow Sidebar */}
                    <div className="lg:col-span-4 space-y-8">
                        <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.08)] overflow-hidden transition-all duration-500 border border-gray-100">
                            <div className="p-6 md:p-8 flex flex-col">
                                {/* Pricing Info */}
                                <div className="flex justify-between items-center mb-6">
                                    <span className="text-gray-500 font-medium text-lg">Pricing</span>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-xl font-bold text-gray-900">AED {project.tokenPriceAED.split(' ')[0]}</span>
                                        <span className="text-gray-400 font-medium text-sm">/tCO2e</span>
                                    </div>
                                </div>

                                <hr className="border-gray-100 mb-6" />

                                {/* Amount Selector */}
                                <div className="mb-2 flex justify-between items-center">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-gray-500 font-medium whitespace-nowrap">Amount</span>
                                        <div className="w-4 h-4 rounded-full bg-gray-400 text-white flex items-center justify-center text-[10px] font-bold cursor-help">?</div>
                                    </div>
                                    <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white ml-2">
                                        <button
                                            onClick={() => setRetirementAmount(prev => Math.max(1, (parseInt(prev) || 1) - 1).toString())}
                                            className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors border-r border-gray-200 font-medium text-lg"
                                        >
                                            -
                                        </button>
                                        <input
                                            type="text"
                                            readOnly
                                            value={retirementAmount || '1'}
                                            className="w-16 h-10 text-center font-semibold text-gray-900 focus:outline-none"
                                        />
                                        <button
                                            onClick={() => setRetirementAmount(prev => ((parseInt(prev) || 0) + 1).toString())}
                                            className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors border-l border-gray-200 font-medium text-lg"
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                                <div className="text-right mb-6">
                                    <span className="text-xs text-gray-500 font-medium">tCO2e</span>
                                </div>

                                <hr className="border-gray-100 mb-6" />

                                {/* Stock Info */}
                                <div className="space-y-3 mb-6">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500 font-medium">Available stock</span>
                                        <span className="text-gray-600 font-medium">{project.availableTokens.toLocaleString()} tCO2e</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500 font-medium">Minimum purchases</span>
                                        <span className="text-gray-600 font-medium">1 tCO2e</span>
                                    </div>
                                </div>

                                <hr className="border-gray-100 mb-6" />

                                {/* Fees Summary */}
                                <div className="space-y-3 mb-6">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500 font-medium">Transaction fee</span>
                                        <span className="text-gray-600 font-medium">AED 1.50</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500 font-medium">VAT</span>
                                        <span className="text-gray-600 font-medium">AED 0.00</span>
                                    </div>
                                    <div className="flex justify-between text-sm pt-2">
                                        <span className="text-gray-500 font-bold">Total</span>
                                        <span className="text-gray-600 font-bold">AED {((parseFloat(project.tokenPriceAED.replace(/,/g, '').split(' ')[0]) || 0) * (parseInt(retirementAmount) || 1) + 1.5).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                    </div>
                                </div>

                                <hr className="border-gray-100 mb-6" />

                                {/* Action Buttons */}
                                <div className="space-y-3">
                                    <button
                                        onClick={handleRetire}
                                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full py-3.5 px-6 font-semibold flex justify-between items-center transition-colors shadow-sm active:scale-[0.98]"
                                    >
                                        <span>Buy now</span>
                                        <span>AED {((parseFloat(project.tokenPriceAED.replace(/,/g, '').split(' ')[0]) || 0) * (parseInt(retirementAmount) || 1) + 1.5).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            alert(`Added ${retirementAmount || 1} tCO2e to your cart!`);
                                        }}
                                        className="w-full bg-white border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 rounded-full py-3.5 px-6 font-semibold flex justify-between items-center transition-colors shadow-sm active:scale-[0.98]"
                                    >
                                        <span>Add to cart</span>
                                        <span className="text-xl leading-none font-medium mb-0.5">+</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Asset Details & Tech Verification */}
                        <div className="bg-white border border-gray-100 rounded-[40px] p-8 space-y-6">
                            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Asset Details</h4>

                            <div className="space-y-4">
                                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                    <div className="p-3 bg-white rounded-xl shadow-sm italic font-black text-emerald-600 text-xs">
                                        TCO2
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Retiring Token</p>
                                        <p className="text-xs font-black text-gray-900 tracking-tight">{project.onChainID}</p>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between px-2">
                                    <div className="flex items-center gap-2">
                                        <Link2 size={16} className="text-gray-400" />
                                        <span className="text-xs font-bold text-gray-500">Registry Source</span>
                                    </div>
                                    <a href={project.explorerURL} target="_blank" rel="noreferrer" className="text-xs font-black text-emerald-600 hover:underline flex items-center gap-1">
                                        View on Registry <ExternalLink size={12} />
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Project Snapshot */}
                        <div className="py-8 space-y-4">
                            <div className="flex items-center gap-3 mb-2">
                                <Zap className="text-emerald-600" size={20} />
                                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600">Project Snapshot</h4>
                            </div>
                            <p className="text-xl font-black leading-tight uppercase text-gray-900">{project.projectType} project in {project.projectLocation.split(',').pop()}</p>
                            <p className="text-xs font-medium text-gray-500 leading-relaxed italic">"Replacing fossil fuels with clean, nature-based sequestration in critical biodiversity hotspots."</p>
                        </div>
                    </div>
                </div >

                {/* FAQ Section */}
                <section className="mt-24 max-w-4xl space-y-12" id="faq">
                    <div className="grid grid-cols-1 gap-4">
                        {[
                            { q: "What does 'retire' mean?", a: "To retire a carbon credit means to permanently take it out of circulation so it can never be sold again. This ensures that the environmental claim you make (like offsetting a flight) is unique and verified." },
                            { q: "Is this recorded on-chain?", a: "Yes, every retirement on our platform is broadcast publicly to the blockchain. This creates a permanent, immutable record that you can use to verify your environmental claims to anyone." },
                            { q: "How are projects verified?", a: "Each project follows strict methodologies from international standards like Verra or Gold Standard. Independent third-party auditors (VVBs) visit the sites to verify the carbon removal before any credits are issued." }
                        ].map((faq, i) => (
                            <div key={i} className="py-6 border-b border-gray-100 transition-colors">
                                <button
                                    onClick={() => setIsFaqOpen(isFaqOpen === i ? null : i)}
                                    className="w-full flex justify-between items-center text-left"
                                >
                                    <span className="text-[11px] font-black uppercase tracking-widest text-gray-800">{faq.q}</span>
                                    <ChevronDown className={`text-gray-400 transition-transform ${isFaqOpen === i ? 'rotate-180' : ''}`} size={20} />
                                </button>
                                {isFaqOpen === i && (
                                    <p className="mt-4 text-sm text-gray-500 leading-relaxed font-medium animate-in fade-in slide-in-from-top-2">
                                        {faq.a}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            </main>

            {/* Retirement Certificate Modal */}
            {showCertificate && retirementCertificate && (
                <RetirementCertificate
                    certificate={retirementCertificate}
                    onClose={() => setShowCertificate(false)}
                />
            )}
        </div>
    );
}
